import { createContext, useContext, useReducer, type Dispatch, type ReactNode } from 'react';
import { emptyAuth, type LocalData } from './types';
import { loadState, removeState, saveState } from './storage';

type AuthAction =
    | { type: 'login'; payload: LocalData }
    | { type: 'hydrate'; payload: LocalData }
    | { type: 'logout' };

function reducer(state: LocalData, action: AuthAction): LocalData {
    switch (action.type) {
        case 'login':
        case 'hydrate':
            return {
                isSignedin: action.payload.isSignedin,
                token: action.payload.token,
                roles: action.payload.roles ?? [],
            };
        case 'logout':
            return { ...emptyAuth };
        default:
            return state;
    }
}

const AuthStateContext = createContext<LocalData | undefined>(undefined);
const AuthDispatchContext = createContext<Dispatch<AuthAction> | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    // Seed from sessionStorage so a page refresh keeps the session.
    const [state, dispatch] = useReducer(reducer, undefined, () => loadState() ?? { ...emptyAuth });

    return (
        <AuthStateContext.Provider value={state}>
            <AuthDispatchContext.Provider value={dispatch}>{children}</AuthDispatchContext.Provider>
        </AuthStateContext.Provider>
    );
}

export function useAuthState(): LocalData {
    const ctx = useContext(AuthStateContext);
    if (ctx === undefined) throw new Error('useAuthState must be used within an AuthProvider');
    return ctx;
}

export function useAuthDispatch(): Dispatch<AuthAction> {
    const ctx = useContext(AuthDispatchContext);
    if (ctx === undefined) throw new Error('useAuthDispatch must be used within an AuthProvider');
    return ctx;
}

// --- Action helpers ------------------------------------------------------
export function loginUser(dispatch: Dispatch<AuthAction>, payload: LocalData): void {
    saveState(payload);
    dispatch({ type: 'login', payload });
}

export function logoutUser(dispatch: Dispatch<AuthAction>): void {
    removeState();
    dispatch({ type: 'logout' });
}
